"use client";

import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { MaintenanceScreen } from "@/components/MaintenanceScreen";
import { ServerUnavailableScreen } from "@/components/ServerUnavailableScreen";
import { BrandLoader } from "@/components/BrandLoader";
import { supabase } from "@/lib/supabase/client";
import { liftGateCurtain, lowerGateCurtain } from "@/lib/gate-curtain";
import { traceFlash } from "@/lib/flash-trace";
import { gatePass } from "@/lib/gate-pass";
import {
  isAccountBlockedResponse,
  onAccountBlocked,
  signalAccountUnblocked,
} from "@/lib/account-block";

// Loaded only for a blocked account: its font loader needs Next's build
// transform, and every other visitor should not pay for the module.
const AccountBlockedScreen = lazy(() =>
  import("@/components/AccountBlockedScreen").then((m) => ({
    default: m.AccountBlockedScreen,
  }))
);

// Cap any auth-gate request at 8s so a hanging fetch never strands the user
// on the centered loading skeleton. 8s is well above normal P99 (~300ms) and
// short enough to fail loud.
const AUTH_FETCH_TIMEOUT_MS = 8000;

/** What actually stopped the gate — each kind gets copy that is TRUE for it
 *  (founder 08-28: "Server temporarily unavailable" on a slow connection "is
 *  untrue"). 'server-error' keeps the screen's default server-outage copy —
 *  the one case the server really is the story. */
type GateTrouble =
  | "offline"
  | "unreachable"
  | "slow-gate"
  | "slow-session"
  | "server-error"
  | "starting";

const TROUBLE_COPY: Record<
  GateTrouble,
  { heading?: string; subtext?: string }
> = {
  "server-error": {},
  offline: {
    heading: "You're offline",
    subtext: "Vestra will reconnect as soon as your internet is back.",
  },
  "slow-gate": {
    heading: "Signing you in is taking longer than usual",
    subtext:
      "Vestra is up — sign-in is responding slowly right now. We keep retrying automatically.",
  },
  // The page could not load its own session yet. Says only what is known: it
  // cannot tell a slow connection from a struggling origin.
  "slow-session": {
    heading: "Signing you in is taking longer than usual",
    subtext: "We keep retrying automatically.",
  },
  unreachable: {
    heading: "Having trouble reaching Vestra",
    subtext:
      "Your connection looks slow or unstable. We keep retrying automatically.",
  },
  // A server that has just started answers /health 503 `starting` until its own
  // live-update channels join (a minute at most). Not an outage: the gate retries
  // quietly first, and only a start that outlasts STARTING_QUIET_MS shows this.
  starting: {
    heading: "Vestra is starting up",
    subtext: "This takes a few seconds. We keep retrying automatically.",
  },
};

/** How long a `starting` server is waited out behind the loader, re-asking every
 *  STARTING_RETRY_MS, before the screen says so (and then with starting-up copy). */
const STARTING_QUIET_MS = 30_000;
const STARTING_RETRY_MS = 2_000;

const isTimeout = (err: unknown): boolean =>
  err instanceof DOMException &&
  (err.name === "TimeoutError" || err.name === "AbortError");

/** Name the trouble by EVIDENCE, not by guess: the browser's own offline word
 *  first; then whether /health (unauthenticated, fast) answers — if it does,
 *  the server is up and only sign-in is slow, and the copy must say so. */
async function classifyGateTrouble(): Promise<GateTrouble> {
  if (typeof navigator !== "undefined" && navigator.onLine === false)
    return "offline";
  try {
    const res = await fetch(`${SERVER_URL}/health`, {
      signal: AbortSignal.timeout(4000),
    });
    if (res.ok) return "slow-gate";
    // A just-started server says `starting` in the body of its 503: it is up and
    // finishing its start, never the server-outage story.
    const body = (await Promise.resolve()
      .then(() => res.json())
      .catch(() => null)) as { status?: unknown } | null;
    return body?.status === "starting" ? "starting" : "server-error";
  } catch {
    return "unreachable";
  }
}

/** A hung promise becomes a TimeoutError, so the trouble classifier gets it —
 *  bounds refreshSession, which has no timeout of its own (08-28: Supabase
 *  /token ran 15–58s and held the skeleton the whole time). */
function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    p,
    new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new DOMException("timed out", "TimeoutError")),
        ms
      )
    ),
  ]);
}

const SERVER_URL =
  process.env.NEXT_PUBLIC_SERVER_URL || "http://localhost:3005";

// Server-side allowlist: `beta_allowlist` table in Supabase. Client no longer
// hardcodes emails — the server returns `{ approved, allowlisted }` from
// /api/invites/check and we trust that answer.
//
// Maintenance mode still has a local override for the founder — it's the only
// way to bypass your own maintenance toggle if the admin dashboard is the
// thing that's down. Keep this small and explicit.
const MAINTENANCE_BYPASS = new Set(["keshav.led@gmail.com"]);

// Defense-in-depth timeout. Primary auth enforcement is server-side in
// middleware. This is a fallback for edge cases where the middleware let
// the request through but the client-side session expired or failed to
// hydrate (e.g. cookie cleared between middleware check and page render).
const AUTH_FALLBACK_MS = 2000;

type GateState =
  | "loading"
  | "beta-gate"
  | "onboarding"
  | "ready"
  | "server-unavailable"
  | "blocked";

// The per-session "gate already resolved to ready" cache lives in
// lib/gate-pass.ts — the arrival ceremony arms it too (the complete call is
// the moment the user becomes onboarded), so the walk into /home starts at
// 'ready' instead of paying another covered verdict.

interface AuthGuardProps {
  children: React.ReactNode;
  /**
   * Rendered while auth + invite check are still resolving. Pass a
   * content-area skeleton so sibling chrome (sidebar, header) can keep
   * painting. Defaults to a full-page spinner for backwards compatibility.
   */
  fallback?: React.ReactNode;
}

export function AuthGuard({ children, fallback }: AuthGuardProps) {
  const { user, session, loading: authLoading, sessionUnreachable } = useAuth();
  const router = useRouter();
  // The page cannot learn its session from our origin and is still retrying
  // (AuthContext): a reason on screen, not a silent curtain.
  const sessionStalled = authLoading && !!sessionUnreachable;
  const redirectTimer = useRef<NodeJS.Timeout | null>(null);

  const [gateState, setGateStateRaw] = useState<GateState>(() =>
    user && gatePass.isReady(user.id) ? "ready" : "loading"
  );
  // Every verdict but 'ready' paints over the whole viewport (the curtain, or
  // one of the .gate-takeover screens below), and none of them leaves a line
  // anywhere — which is why a whole-screen flash could not be placed from the
  // logs (founder, 09-21). One trace per verdict, nothing else changed.
  const setGateState = useCallback((next: GateState) => {
    if (next !== "ready") traceFlash("gate", next);
    setGateStateRaw(next);
  }, []);
  // Did this mount start optimistically from the session cache? If so, a
  // transient revalidation error (5xx / network) must not knock the user out of
  // content they can already see — only a definitive negative (not-approved /
  // dead session) downgrades. On a cold mount this is false, so first-load
  // error handling is unchanged.
  const startedReadyRef = useRef(gateState === "ready");
  // When the gate first found the server `starting`; null once a verdict lands.
  const startingSinceRef = useRef<number | null>(null);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [trouble, setTrouble] = useState<GateTrouble | null>(null);
  // Bumped by the unavailable screen's Retry: the gate effect is keyed on it,
  // so a retry RE-RUNS the check. Without this, retry parked the user on the
  // skeleton until the session object happened to change (founder 08-28:
  // "still not working").
  const [retryTick, setRetryTick] = useState(0);
  // A block that lands mid-session: the server sheds this tab's socket and
  // refuses the reconnect with ACCOUNT_BLOCKED (or a fetch answers
  // account_blocked), which re-runs the gate now. While the screen already
  // shows, further signals are ignored so a refused re-dial cannot loop.
  const gateStateRef = useRef(gateState);
  useEffect(() => {
    gateStateRef.current = gateState;
  }, [gateState]);
  // Every retry path bumps retryTick, so the tick alone cannot say which one
  // re-ran the gate — this marks the server-signalled one for the trace below.
  const blockedSignalRef = useRef(false);
  // The gate effect's dependencies as of its last run, so a re-run can name
  // the one that changed.
  const gateDepsRef = useRef({ user, session, retryTick });
  useEffect(
    () =>
      onAccountBlocked(() => {
        if (gateStateRef.current === "blocked") return;
        blockedSignalRef.current = true;
        setRetryTick((t) => t + 1);
      }),
    []
  );

  // Gate curtain (the branded cover baked DOWN into the prerendered shell
  // HTML): its state derives from the gate verdict, both directions — down
  // while 'loading' (no chrome before a verdict, ever), lifted on any verdict
  // ('ready' reveals the app; every other verdict paints its own opaque
  // takeover later in the DOM, so lifting is invisible). No pre-paint cache
  // exists anymore: a local mark lied after an out-of-band account deletion
  // (founder, 08-03). Session-cached remounts start at 'ready', so in-app
  // navigation never sees the curtain.
  useEffect(() => {
    if (gateState === "loading" && !sessionStalled) lowerGateCurtain();
    else liftGateCurtain();
  }, [gateState, sessionStalled]);

  // Navigate in an effect, not in render: the 'onboarding' and 'beta-gate'
  // branches below stay mounted across many renders while the route
  // transition runs. Not approved → the waitlist, every new sign-up's front
  // door (spec 2026-10-17-waitlist-front-door).
  useEffect(() => {
    // Not approved, or approved but not set up yet: the waitlist sorts both (it sets up
    // whoever is let in, then home; the old onboarding is gone, founder 2026-10-18).
    if (gateState === "onboarding" || gateState === "beta-gate")
      router.replace("/waitlist");
  }, [gateState, router]);

  useEffect(() => {
    if (!authLoading && !user) {
      redirectTimer.current = setTimeout(() => {
        router.replace("/login");
      }, AUTH_FALLBACK_MS);
    }

    return () => {
      if (redirectTimer.current) {
        clearTimeout(redirectTimer.current);
        redirectTimer.current = null;
      }
    };
  }, [authLoading, user, router]);

  // Server-side approval + onboarding check.
  // Single endpoint (/api/invites/check) tells us everything we need:
  //   - approved: can the user enter the app at all?
  //   - allowlisted: server-side beta_allowlist membership
  //   - onboardingStatus: signed_up | invited | onboarded
  //   - reason: redeemed | allowlist | not_invited | user_not_found
  useEffect(() => {
    if (authLoading || !user || !session) return;

    // A re-check can end in the curtain or a takeover, so name the dependency
    // that caused it: a token refresh hands a fresh session object and re-runs
    // this effect silently, which makes it the leading suspect for a flash
    // mid-session. The first run of a mount is not a re-check.
    const prev = gateDepsRef.current;
    const why =
      prev.retryTick !== retryTick
        ? blockedSignalRef.current
          ? "blocked-signal"
          : "retry"
        : prev.session !== session
          ? "session"
          : prev.user !== user
            ? "user"
            : null;
    if (why) traceFlash("gate_recheck", why);
    blockedSignalRef.current = false;
    gateDepsRef.current = { user, session, retryTick };

    let cancelled = false;
    let quietRetry: ReturnType<typeof setTimeout> | null = null;

    async function checkStatus() {
      // Maintenance mode check (public endpoint, no auth). Fire-and-forget —
      // it can only ever LOWER a wall, never open one, so it must not sit in
      // front of the verdict: awaiting it serialized a full extra round trip
      // into every cold load's curtain time (founder 08-04, "reduce it").
      void (async () => {
        try {
          const maintRes = await fetch(`${SERVER_URL}/api/admin/maintenance`);
          if (maintRes.ok) {
            const maintData = await maintRes.json();
            if (!cancelled) setMaintenanceMode(maintData.enabled === true);
          }
        } catch {
          // Fail open — if we can't reach the maintenance endpoint, don't block.
        }
      })();

      // Issue the gated fetch with a fresh access token. On 401, transparently
      // try a Supabase session refresh and retry once — this handles the
      // common "tab was idle long enough that the in-memory access_token
      // expired but the refresh token is still valid" path. If even after
      // refresh we 401, the session is genuinely dead — sign the user out
      // cleanly and bounce them to /login.
      const checkOnce = async (token: string): Promise<Response> =>
        fetch(`${SERVER_URL}/api/invites/check`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: AbortSignal.timeout(AUTH_FETCH_TIMEOUT_MS),
        });

      // One quiet second chance on a TIMEOUT only: a single slow hop must not
      // take over the page. A network error (TypeError) fails fast — retrying
      // a dead network buys nothing but delay.
      const checkGate = async (token: string): Promise<Response> => {
        try {
          return await checkOnce(token);
        } catch (err) {
          if (!isTimeout(err)) throw err;
          return await checkOnce(token);
        }
      };

      const bounceToLogin = async () => {
        gatePass.clear(); // dead session — never let a stale "ready" linger
        lowerGateCurtain(); // cover the chrome for the whole teardown + redirect
        try {
          await supabase.auth.signOut();
        } catch {
          /* best effort */
        }
        if (!cancelled) router.replace("/login");
      };

      try {
        let res = await checkGate(session!.access_token);

        if (cancelled) return;

        if (res.status === 401) {
          // First 401 — attempt one transparent refresh.
          // Bounded: a refresh that HANGS is trouble to classify, not a dead
          // session — only Supabase ANSWERING "no" signs the user out.
          const { data: refreshed, error: refreshErr } = await withTimeout(
            supabase.auth.refreshSession(),
            AUTH_FETCH_TIMEOUT_MS
          );
          if (cancelled) return;
          if (refreshErr || !refreshed.session) {
            await bounceToLogin();
            return;
          }
          res = await checkOnce(refreshed.session.access_token);
          if (cancelled) return;
          if (res.status === 401) {
            // Refresh succeeded but server still rejects — definitively dead.
            await bounceToLogin();
            return;
          }
        }

        if (await isAccountBlockedResponse(res)) {
          if (cancelled) return;
          // Definitive, not a blip: it downgrades even a started-ready session.
          // The content is gone now, so the started-ready leniency is too: a
          // failed re-check after an unblock must show, not strand the loader.
          gatePass.clear();
          startedReadyRef.current = false;
          setGateState("blocked");
          return;
        }

        if (res.ok) {
          startingSinceRef.current = null;
          const data = (await res.json()) as {
            approved: boolean;
            allowlisted: boolean;
            onboardingStatus: "signed_up" | "invited" | "onboarded";
            reason: "redeemed" | "allowlist" | "not_invited" | "user_not_found";
          };
          if (cancelled) return;

          if (data.approved && data.onboardingStatus === "onboarded") {
            // Success: cache the pass so a remount (e.g. group-crossing) starts
            // at 'ready' and skips the skeleton. This branch also RE-ARMS the
            // cache if an earlier transient blip had cleared it.
            gatePass.arm(user!.id);
            setGateState("ready");
          } else if (!startedReadyRef.current) {
            // Cold mount + a negative result → take the user to the right
            // screen. Allowlisted users may be 'signed_up' (never redeemed a
            // code, so status was never bumped) — still needs onboarding.
            gatePass.clear();
            // The hop to /onboarding carries its beat: an approved user has
            // redeemed, so "You're in." is exactly where they stand — without
            // the handoff the fresh shell reopens on the sign-in beat and
            // "Signing you in…" flashes mid-ceremony (founder, 08-05).
            setGateState(data.approved ? "onboarding" : "beta-gate");
          }
          // else: this mount started optimistically from the session cache and
          // the gate transiently reports not-approved/not-onboarded. Do NOT
          // null the cache or yank the user out of content they can already
          // see — a stale/transient read must not flash the skeleton on the
          // next remount. The next revalidation reconciles; server middleware
          // remains the hard access gate regardless.
        } else if (res.status >= 500) {
          // Server is up but errored — transient. On a cold mount, show the
          // reconnection screen. On an optimistic remount, keep the content the
          // user already has rather than yanking them to an error screen on a
          // blip — the next revalidation recovers.
          if (!startedReadyRef.current) {
            setTrouble("server-error");
            setGateState("server-unavailable");
          }
        } else {
          // 4xx other than 401: treat as not approved — but only downgrade on a
          // cold mount. A started-ready user stays put through a transient 4xx;
          // the cache survives so the next remount doesn't re-flash the loader.
          if (!startedReadyRef.current) {
            gatePass.clear();
            setGateState("beta-gate");
          }
        }
      } catch {
        if (cancelled) return;
        // AbortSignal.timeout throws TimeoutError; network failures throw
        // TypeError; a hung refresh is bounded into TimeoutError above. The
        // screen's copy is chosen by EVIDENCE (browser offline / health probe)
        // so a slow connection is never presented as our server being down.
        // Same optimistic-remount leniency as the 5xx branch above.
        if (startedReadyRef.current) return;
        const kind = await classifyGateTrouble();
        if (cancelled) return;
        // A server finishing its start is not trouble: keep the loader and ask
        // again shortly. Only a start that outlasts the quiet budget shows the
        // screen, and then with starting-up copy, never the error copy.
        if (kind === "starting") {
          startingSinceRef.current ??= Date.now();
          if (Date.now() - startingSinceRef.current < STARTING_QUIET_MS) {
            quietRetry = setTimeout(
              () => setRetryTick((t) => t + 1),
              STARTING_RETRY_MS
            );
            return;
          }
        }
        setTrouble(kind);
        setGateState("server-unavailable");
      }
    }

    checkStatus();

    return () => {
      cancelled = true;
      if (quietRetry) clearTimeout(quietRetry);
    };
  }, [authLoading, user, session, retryTick]);

  // The session itself cannot be fetched yet. Lifts on its own: the fetch
  // keeps retrying (and wakes on `online`), and the first auth event clears
  // sessionUnreachable. A manual Retry reloads, which asks again from scratch.
  if (sessionStalled) {
    const kind: GateTrouble =
      typeof navigator !== "undefined" && navigator.onLine === false
        ? "offline"
        : "slow-session";
    return (
      <div className="gate-takeover fixed inset-0 z-[1200] overflow-y-auto bg-bg-base">
        <ServerUnavailableScreen
          heading={TROUBLE_COPY[kind].heading}
          subtext={TROUBLE_COPY[kind].subtext}
          autoRetry={false}
          onServerBack={() => window.location.reload()}
        />
      </div>
    );
  }

  // Still loading auth → use caller-provided fallback (content-area skeleton)
  // so sibling chrome can keep painting. Falls back to full-page spinner
  // when no fallback is provided (legacy callers).
  if (authLoading || !user) {
    return <>{fallback ?? <BrandLoader />}</>;
  }

  // Maintenance mode — block everyone except admin (runtime toggle + env var fallback).
  // Full-screen takeover: this is a "wrong place" state, not a "still loading" one.
  const isInMaintenance =
    maintenanceMode || process.env.NEXT_PUBLIC_MAINTENANCE_MODE === "true";
  if (isInMaintenance && user && !MAINTENANCE_BYPASS.has(user.email ?? "")) {
    return (
      <div className="gate-takeover fixed inset-0 z-[1200] overflow-y-auto bg-bg-base">
        <MaintenanceScreen />
      </div>
    );
  }

  // Checking approval status from server. Two very different audiences share
  // this state:
  //  - A user this session has ALREADY passed through the gate (_gateReadyUserId)
  //    starts at 'ready' and never lands here — remounts stay instant.
  //  - An UNKNOWN user (fresh sign-in, new browser) holds a FULL-SCREEN cover
  //    until the verdict. The first fix here held only this content region,
  //    but the sidebar/dock render as SIBLINGS outside the guard — so a
  //    brand-new account still saw the office chrome flash before the invite
  //    gate took over (founder, live 2026-08-02 and again 08-03). The first
  //    pixel a new user sees must be the door, not any part of home, so the
  //    cover must blanket the whole viewport — same device the 'onboarding'
  //    branch below uses to hide chrome during its redirect. Approved users
  //    on a fresh browser pay one covered beat (~300ms P99), then cache
  //    'ready' for the rest of the session.
  //
  // NOT the content-area `fallback` here: this takeover paints directly over
  // the curtain, and the two must be pixel-identical (sunbeam @ 96) so the
  // handoff is invisible — the fallback's skeleton dot replacing the mark was
  // the founder's "stupid white screen with the stupid dot" (08-04).
  if (gateState === "loading") {
    return (
      <div className="gate-takeover fixed inset-0 z-[1200] overflow-y-auto bg-bg-base">
        <BrandLoader variant="sunbeam" size={96} />
      </div>
    );
  }

  // Blocked by an admin — full-screen takeover. The screen lifts itself when
  // the block is gone and hands back here to re-run the gate and re-dial the
  // socket (socket.io never retries the refusal on its own).
  if (gateState === "blocked") {
    return (
      <div className="gate-takeover fixed inset-0 z-[1200] overflow-y-auto bg-bg-base">
        <Suspense fallback={<BrandLoader variant="sunbeam" size={96} />}>
          <AccountBlockedScreen
            onUnblocked={() => {
              setRetryTick((t) => t + 1);
              setGateState("loading");
              signalAccountUnblocked();
            }}
          />
        </Suspense>
      </div>
    );
  }

  // Server unreachable — full-screen takeover (intentional)
  if (gateState === "server-unavailable") {
    return (
      <div className="gate-takeover fixed inset-0 z-[1200] overflow-y-auto bg-bg-base">
        <ServerUnavailableScreen
          heading={TROUBLE_COPY[trouble ?? "server-error"].heading}
          subtext={TROUBLE_COPY[trouble ?? "server-error"].subtext}
          onServerBack={() => {
            setTrouble(null);
            setRetryTick((t) => t + 1);
            setGateState("loading");
          }}
        />
      </div>
    );
  }

  // Not approved, or approved but not set up yet: the effect above sends them to
  // the waitlist (it sets up whoever is let in, then home); meanwhile the same
  // brand cover as the loading state holds the screen, so neither the app's
  // chrome nor any old screen ever paints for them.
  if (gateState === "beta-gate" || gateState === "onboarding") {
    return (
      <div className="gate-takeover fixed inset-0 z-[1200] overflow-y-auto bg-bg-base">
        <BrandLoader variant="sunbeam" size={96} />
      </div>
    );
  }

  return <>{children}</>;
}
